import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Twitter, Youtube, Linkedin, Wifi, Navigation, Coffee, Sparkles } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import TikTokIcon from './icons/TikTokIcon';
import { GoogleReviewCardCompact } from './GoogleReviewCard';
import api from '../utils/apiClient';

// Google Maps directions URL
const DIRECTIONS_URL = "https://www.google.com/maps/dir/?api=1&destination=Shop+3,+Concave+Road,+Namaka,+Nadi,+Fiji&travelmode=driving";

// Default values (fallback if CMS data not loaded)
const DEFAULT_LOGO = "/images/bulaccino-logo-white.png";

// Social icon mapping
const SOCIAL_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  tiktok: TikTokIcon,
  twitter: Twitter,
  youtube: Youtube,
  linkedin: Linkedin,
  // Add more as needed
};

export default function Footer() {
  const { getFooter, loading } = useContent();
  const footer = getFooter();
  const navigate = useNavigate();
  
  // State for contact info from settings API
  const [contactInfo, setContactInfo] = useState(null);
  
  // Handle WiFi banner click - navigate to loyalty and scroll to top
  const handleWifiClick = (e) => {
    e.preventDefault();
    navigate('/loyalty');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };
  
  // Fetch contact info from settings API (same source as Contact page)
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const res = await api.get('/contact-info');
        setContactInfo(res.data);
      } catch (err) {
        console.log('Using default contact info in footer');
      }
    };
    fetchContactInfo();
  }, []);

  // Extract data from CMS with fallbacks
  const branding = footer.branding || {
    logo: DEFAULT_LOGO,
    tagline: "Your tropical escape in the heart of Fiji. Fresh coffee, vibrant flavors, and island hospitality."
  };
  
  // Build social links from contact info or defaults
  const socialLinks = contactInfo ? [
    contactInfo.instagram && { platform: 'instagram', url: contactInfo.instagram },
    contactInfo.facebook && { platform: 'facebook', url: contactInfo.facebook },
    contactInfo.tiktok && { platform: 'tiktok', url: contactInfo.tiktok }
  ].filter(Boolean) : (footer.socialLinks || [
    { platform: 'instagram', url: 'https://instagram.com/bulaccinocafe' },
    { platform: 'facebook', url: 'https://facebook.com/bulaccinocafe' },
    { platform: 'tiktok', url: 'https://www.tiktok.com/@bulaccinocafe' }
  ]);
  
  // Use contact info from settings API if available
  const contact = contactInfo ? {
    title: "Visit Us",
    address: contactInfo.address + (contactInfo.address_extra ? `\n${contactInfo.address_extra}` : ''),
    phone: contactInfo.phone,
    email: contactInfo.email
  } : (footer.contact || {
    title: "Visit Us",
    address: "Shop 3 at Concave Road\nNamaka, Nadi, Fiji",
    phone: "+679 675 0102",
    email: "administrator@bulaccinocafe.com"
  });
  
  // Parse hours from contact info or use defaults
  const hours = contactInfo?.hours ? {
    title: "Opening Hours",
    hours: [{ days: contactInfo.hours.split(':')[0]?.trim() || "Daily", time: contactInfo.hours.split(':').slice(1).join(':')?.trim() || contactInfo.hours }],
    note: "Fresh brews and tropical bites await you every morning in paradise."
  } : (footer.hours || {
    title: "Opening Hours",
    hours: [{ days: "Daily", time: "6:30 AM - 8:00 PM" }],
    note: "Fresh brews and tropical bites await you every morning in paradise."
  });
  
  const navLinks = footer.navLinks || {
    title: "Quick Links",
    links: [
      { label: "Coffee Pass", url: "/bula-coffee-club", highlight: true },
      { label: "Our Menu", url: "/menu" },
      { label: "Order Online", url: "/order" },
      { label: "Our Story", url: "/about" },
      { label: "Loyalty Rewards", url: "/loyalty" },
      { label: "Blog", url: "/blog" },
      { label: "FAQ", url: "/faq" },
      { label: "Contact Us", url: "/contact" }
    ]
  };
  
  const custom = footer.custom || {
    copyrightText: "© {year} Bulaccino Cafe. All rights reserved.",
    additionalText: "Made with love in Fiji"
  };

  // Process copyright text (replace {year} with current year)
  const copyrightText = custom.copyrightText.replace('{year}', new Date().getFullYear());

  // Use env variable for logo or CMS value
  const logoUrl = process.env.REACT_APP_LOGO_URL || branding.logo || DEFAULT_LOGO;

  return (
    <footer className="bg-card dark:bg-muted/30 border-t border-border" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <img 
              src={logoUrl} 
              alt={branding.siteName || "Bulaccino Cafe"} 
              className="h-20 w-auto dark:drop-shadow-[0_0_10px_rgba(255,255,255,1)] dark:filter"
              data-testid="footer-logo"
            />
            <p className="text-muted-foreground leading-relaxed">
              {branding.tagline}
            </p>
            <div className="flex gap-4">
              {socialLinks.map((link, index) => {
                const IconComponent = SOCIAL_ICONS[link.platform] || Instagram;
                return (
                  <a 
                    key={`social-${link.platform}`}
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-white transition-colors text-foreground"
                    data-testid={`footer-${link.platform}`}
                    aria-label={link.label || link.platform}
                  >
                    <IconComponent className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-xl mb-6 text-foreground">{navLinks.title}</h4>
            <ul className="space-y-3">
              {navLinks.links.map((link) => (
                <li key={`nav-${link.label}-${link.url}`}>
                  <Link 
                    to={link.url} 
                    className={`transition-colors flex items-center gap-2 ${
                      link.highlight 
                        ? 'text-primary font-medium hover:text-primary/80' 
                        : 'text-muted-foreground hover:text-primary'
                    }`}
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.highlight && <Sparkles className="w-4 h-4" />}
                    {link.label}
                    {link.highlight && (
                      <span className="px-1.5 py-0.5 bg-accent text-white text-[10px] font-bold rounded-full">NEW</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-xl mb-6 text-foreground">{contact.title}</h4>
            <ul className="space-y-4">
              {contact.address && (
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <a 
                    href={DIRECTIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors whitespace-pre-line cursor-pointer"
                    data-testid="footer-address"
                  >
                    {contact.address}
                  </a>
                </li>
              )}
              {contact.phone && (
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-accent flex-shrink-0" />
                  <a 
                    href={`tel:${contact.phone.replace(/\s/g, '')}`} 
                    className="text-muted-foreground hover:text-primary transition-colors"
                    data-testid="footer-phone"
                  >
                    {contact.phone}
                  </a>
                </li>
              )}
              {contact.email && (
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-accent flex-shrink-0" />
                  <a 
                    href={`mailto:${contact.email}`} 
                    className="text-muted-foreground hover:text-primary transition-colors"
                    data-testid="footer-email"
                  >
                    {contact.email}
                  </a>
                </li>
              )}
              {/* Get Directions Link */}
              <li className="flex items-center gap-3 pt-2">
                <Navigation className="w-5 h-5 text-primary flex-shrink-0" />
                <a 
                  href={DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 transition-colors font-medium"
                  data-testid="footer-directions"
                >
                  Get Directions
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-heading text-xl mb-6 text-foreground">{hours.title}</h4>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
              <div className="text-muted-foreground">
                {hours.hours.map((schedule) => (
                  <div key={`hours-${schedule.days}`}>
                    <p className="font-medium text-foreground">{schedule.days}</p>
                    <p>{schedule.time}</p>
                  </div>
                ))}
              </div>
            </div>
            {hours.note && (
              <div className="mt-6 p-4 bg-primary/10 rounded-2xl">
                <p className="text-sm text-foreground">
                  {hours.note}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-border">
          {/* Action Banners - Coffee Pass, WiFi and Google Review */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 max-w-4xl mx-auto">
            {/* Coffee Pass Banner */}
            <Link 
              to="/bula-coffee-club"
              className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-primary/10 to-amber-500/10 hover:from-primary/20 hover:to-amber-500/20 rounded-xl transition-colors group cursor-pointer border border-primary/20"
              data-testid="footer-coffee-pass-btn"
            >
              <Coffee className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-foreground block">Bula Coffee Pass</span>
                <span className="text-xs text-muted-foreground">$95/year for FREE daily coffee</span>
              </div>
              <span className="text-xs font-medium text-primary group-hover:underline whitespace-nowrap flex-shrink-0">Join →</span>
            </Link>
            
            {/* WiFi Banner */}
            <button 
              onClick={handleWifiClick}
              className="flex items-center gap-3 px-4 py-3 bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors group cursor-pointer border border-primary/20 text-left"
              data-testid="footer-wifi-btn"
            >
              <Wifi className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-foreground block">Free WiFi</span>
                <span className="text-xs text-muted-foreground">For Loyalty Members</span>
              </div>
              <span className="text-xs font-medium text-primary group-hover:underline whitespace-nowrap flex-shrink-0">Join Free →</span>
            </button>
            
            {/* Google Review Card */}
            <div className="flex items-center">
              <GoogleReviewCardCompact />
            </div>
          </div>
          
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-6">
            <Link 
              to="/privacy-policy" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="footer-privacy-policy"
            >
              Privacy Policy
            </Link>
            <span className="text-muted-foreground/50">|</span>
            <Link 
              to="/terms-of-use" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="footer-terms-of-use"
            >
              Terms of Use
            </Link>
            <span className="text-muted-foreground/50">|</span>
            <Link 
              to="/cookies" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="footer-cookie-policy"
            >
              Manage Cookies
            </Link>
            <span className="text-muted-foreground/50">|</span>
            <Link 
              to="/careers" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="footer-careers"
            >
              Careers
            </Link>
            <span className="text-muted-foreground/50">|</span>
            <Link 
              to="/partners" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="footer-partners"
            >
              Our Partners
            </Link>
            <span className="text-muted-foreground/50">|</span>
            <Link 
              to="/press" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="footer-press"
            >
              Press
            </Link>
            <span className="text-muted-foreground/50">|</span>
            <Link 
              to="/partner-program" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="footer-partner-program"
            >
              Partner Program
            </Link>
          </div>
          
          {/* Location Disclaimer */}
          <p className="text-xs text-muted-foreground/70 text-center mb-4 max-w-2xl mx-auto">
            All promotions and menu pricing are exclusive to Bulaccino Cafe located at Shop 3 at Concave Road, Namaka, Nadi, Fiji and not any other location.
          </p>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-sm" data-testid="footer-copyright">
              {copyrightText}
            </p>
            {custom.additionalText && (
              <p className="text-muted-foreground text-sm">
                {custom.additionalText}
              </p>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
